import {
    ApplicationRef,
    createComponent,
    EnvironmentInjector,
    inject,
    Injectable,
    Injector,
    Type
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import '@shui/core/sh-modal';
import { ShuiModalRef } from './shui-modal-ref';
import { SHUI_MODAL_CONFIG, ShuiModalConfig } from '../tokens/shui-modal-config.token';

@Injectable({ providedIn: 'root' })
export class DialogService {
    private readonly appRef = inject(ApplicationRef);
    private readonly injector = inject(EnvironmentInjector);
    private readonly translateService = inject(TranslateService);

    public open<TResult = unknown, TData = unknown>(
        component: Type<unknown>,
        config?: ShuiModalConfig<TData>
    ): ShuiModalRef<TResult> {
        const modalRef = new ShuiModalRef<TResult>();

        // Capture the currently focused element to restore after modal closes
        const previouslyFocusedElement = document.activeElement as HTMLElement | null;

        // Create the sh-modal host element
        const modalElement = document.createElement('sh-modal');
        modalElement.setAttribute('modal-width', config?.width ?? 'fit-content');
        modalElement.setAttribute('modal-height', 'fit-content');
        modalElement.style.setProperty('--sh-public-modal-max-width', '50vw');

        if (config?.title) {
            modalElement.setAttribute('label', this.translateService.instant(config.title));
        }

        if (config?.sticky) {
            modalElement.setAttribute('sticky', '');
        }

        // Append to body
        document.body.appendChild(modalElement);

        // Create a custom injector providing ShuiModalRef and SHUI_MODAL_CONFIG
        const modalInjector = Injector.create({
            parent: this.injector,
            providers: [
                { provide: ShuiModalRef, useValue: modalRef },
                { provide: SHUI_MODAL_CONFIG, useValue: config ?? {} }
            ]
        });

        // Dynamically create the dialog component inside the modal
        const componentRef = createComponent(component, {
            environmentInjector: this.injector,
            elementInjector: modalInjector,
            hostElement: modalElement
        });

        this.appRef.attachView(componentRef.hostView);

        // Show the modal
        modalElement.setAttribute('visible', '');

        // Focus the modal so Tab navigates into its content
        setTimeout(() => modalElement.focus(), 0);

        let cleaned = false;

        // Cleanup function — guarded to run only once
        const cleanup = (): void => {
            if (cleaned) return;
            cleaned = true;
            modalElement.removeEventListener('modal-closed', onModalClosed);
            modalElement.removeAttribute('visible');
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
            modalElement.remove();

            // Restore focus to the element that was focused before the modal opened
            if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
                setTimeout(() => {
                    previouslyFocusedElement.focus();
                    // sh-radio-button only shows focus ring on Tab keyup — simulate it
                    if (previouslyFocusedElement.tagName === 'SH-RADIO-BUTTON') {
                        previouslyFocusedElement.dispatchEvent(
                            new KeyboardEvent('keyup', { code: 'Tab', key: 'Tab', bubbles: true })
                        );
                    }
                }, 0);
            }
        };

        // Listen for modal-closed event (backdrop click or X button)
        const onModalClosed = (): void => {
            cleanup();
            modalRef.close(undefined);
        };

        modalElement.addEventListener('modal-closed', onModalClosed);

        // When the dialog component calls modalRef.close(), clean up
        modalRef.afterClosed.subscribe({
            complete: () => {
                cleanup();
            }
        });

        return modalRef;
    }
}
